import React, { useMemo } from 'react';
import { Routes, Route, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import ErrorBoundary from "../Components/Common/ErrorBoundary";
import { AuthUserPayload, getStoredAuth } from "../helpers/auth_storage";

//Layouts
import NonAuthLayout from "../Layouts/NonAuthLayout";
import VerticalLayout from "../Layouts/index";
import AdminVerticalLayout from "../Layouts/AdminVerticalLayout";

//routes
import {
  authProtectedRoutes,
  publicRoutes,
  superAdminProtectedRoutes,
} from "./allRoutes";
import AuthProtected from './AuthProtected';
import RoleGuard from './RoleGuard';
import PlanGuard from './PlanGuard';
import SuperAdminProtected from './SuperAdminProtected';

const Index = () => {
    const location = useLocation();
    const loginUser = useSelector(
        (state: { Login: { user: AuthUserPayload | Record<string, never> } }) =>
            state.Login.user
    );
    const tenantScopeKey = useMemo(() => {
        const fromRedux =
            loginUser &&
            typeof loginUser === "object" &&
            "user" in loginUser &&
            loginUser.user &&
            typeof loginUser.user === "object"
                ? (loginUser.user as { tenant_id?: number }).tenant_id
                : undefined;
        const tenantId = fromRedux ?? getStoredAuth()?.user?.tenant_id;
        return tenantId != null ? `tenant-${tenantId}` : "tenant-none";
    }, [loginUser]);

    return (
        <ErrorBoundary resetKey={`${location.pathname}:${tenantScopeKey}`}>
            <Routes>
                <Route>
                    {publicRoutes.map((route, idx) => (
                        <Route
                            path={route.path}
                            element={
                                <NonAuthLayout>
                                    {route.component}
                                </NonAuthLayout>
                            }
                            key={idx}
                        />
                    ))}
                </Route>

                <Route>
                    {superAdminProtectedRoutes.map((route, idx) => (
                        <Route
                            path={route.path}
                            element={
                                <SuperAdminProtected>
                                    <AdminVerticalLayout>{route.component}</AdminVerticalLayout>
                                </SuperAdminProtected>
                            }
                            key={`super-admin-${idx}`}
                        />
                    ))}
                </Route>

                <Route>
                    {authProtectedRoutes.map((route, idx) => {
                        let protectedContent = route.component;

                        if (route.allowedRoles) {
                            protectedContent = (
                                <RoleGuard allowedRoles={route.allowedRoles}>
                                    {protectedContent}
                                </RoleGuard>
                            );
                        }

                        if (route.requiredPlanFeature) {
                            protectedContent = (
                                <PlanGuard feature={route.requiredPlanFeature}>
                                    {protectedContent}
                                </PlanGuard>
                            );
                        }

                        return (
                            <Route
                                path={route.path}
                                element={
                                    <AuthProtected key={tenantScopeKey}>
                                        <VerticalLayout>{protectedContent}</VerticalLayout>
                                    </AuthProtected>
                                }
                                key={idx}
                            />
                        );
                    })}
                </Route>
            </Routes>
        </ErrorBoundary>
    );
};

export default Index;
